/**
 * Project Results sub-page — completed-execution results for the project
 * selected in the context bar, drilling into a specific run/execution.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import { useRuns } from '../../../api/media-sim/runs';
import { useRunExecutions } from '../../../api/media-sim/executions';
import { StatusBadge } from '../../../components/media-sim/shared/StatusBadge';
import { SimulationResults } from '../../../components/media-sim/executions/SimulationResults';
import { ExecutionReflectionsView } from '../../../components/media-sim/executions/ExecutionReflectionsView';
import { ProjectScopeGate } from './ProjectScopeGate';
import type { Project, Run } from '../../../types/media-sim';

function RunExecutionList({
  projectId,
  run,
  onSelect,
}: {
  projectId: string;
  run: Run;
  onSelect: (sel: { runId: string; executionId: string }) => void;
}) {
  const { theme } = useTheme();
  const { executions } = useRunExecutions(projectId, run.id);
  const completedExecutions = executions.filter((e) => e.status === 'completed');

  if (completedExecutions.length === 0) return null;

  return (
    <Card variant="outlined">
      <Card.Header title={run.run_name || 'Unnamed Run'} />
      <Card.Body>
        <Stack gap="xs">
          {completedExecutions.map((exec) => (
            <Flex key={exec.id} justify="between" align="center" style={{ padding: `${spacing.xs} 0` }}>
              <Flex align="center" gap="sm">
                <StatusBadge status={exec.status} size="sm" />
                <span style={{ fontSize: '0.85rem', color: theme.colors.text.primary }}>
                  {exec.execution_type === 'sample' ? 'Sample' : 'Full Simulation'}
                </span>
              </Flex>
              <Button variant="ghost" size="sm" onClick={() => onSelect({ runId: run.id, executionId: exec.id })}>
                View Results
              </Button>
            </Flex>
          ))}
        </Stack>
      </Card.Body>
    </Card>
  );
}

function ResultsContent({ project }: { project: Project }) {
  const { theme } = useTheme();
  const projectId = project.id;
  const { data: runs } = useRuns(projectId);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link: /simulations/results?runId=...&executionId=... opens straight
  // to that execution's results (used by "View Sample Results" and the run's
  // "View Results & Articles" buttons so both paths land on this one view).
  const linkedRunId = searchParams.get('runId');
  const linkedExecutionId = searchParams.get('executionId');
  const [selectedRun, setSelectedRun] = useState<{ runId: string; executionId: string } | null>(
    linkedRunId && linkedExecutionId
      ? { runId: linkedRunId, executionId: linkedExecutionId }
      : null
  );

  const clearSelection = () => {
    setSelectedRun(null);
    // Drop the deep-link params so "Back to Results" returns to the list.
    if (linkedRunId || linkedExecutionId) setSearchParams({}, { replace: true });
  };

  if (selectedRun) {
    return (
      <Stack gap="lg">
        <Flex justify="start">
          <Button variant="ghost" onClick={clearSelection}>
            &larr; Back to Results
          </Button>
        </Flex>

        <SimulationResults
          projectId={projectId}
          runId={selectedRun.runId}
          executionId={selectedRun.executionId}
        />

        <div style={{ borderTop: `1px solid ${theme.colors.border.default}`, margin: `${spacing.md} 0` }} />

        <ExecutionReflectionsView
          projectId={projectId}
          runId={selectedRun.runId}
          executionId={selectedRun.executionId}
        />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
        Results — {project.project_name}
      </h1>
      {!runs || runs.length === 0 ? (
        <Card variant="outlined">
          <Card.Body>
            <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
              No runs yet. Create a run and execute a simulation to see results.
            </p>
          </Card.Body>
        </Card>
      ) : (
        runs.map((run) => (
          <RunExecutionList key={run.id} projectId={projectId} run={run} onSelect={setSelectedRun} />
        ))
      )}
    </Stack>
  );
}

export function ProjectResultsPage() {
  return <ProjectScopeGate>{(project) => <ResultsContent project={project} />}</ProjectScopeGate>;
}
